import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useState } from "react";
import { toast } from "react-toastify";

// Only the first few KB are needed to read a CSV's header row.
const HEADER_PEEK_BYTES = 4096;

// Classifies one file against sourceConfig.upload.fileTypes by its header
// row — e.g. IMDb's ratings and watchlist exports share most columns but
// differ enough (Position, Your Rating/Date Rated placement) to tell apart.
async function detectFileType(file, fileTypes) {
  const headerText = await file.slice(0, HEADER_PEEK_BYTES).text();
  const firstLine = headerText.split(/\r?\n/)[0] || "";
  const headers = firstLine
    .split(",")
    .map((h) => h.trim().replace(/^"|"$/g, ""));

  return fileTypes.find((type) => {
    const hasRequired = type.requiredHeaders.every((h) => headers.includes(h));
    const hasExcluded = (type.excludeHeaders || []).some((h) =>
      headers.includes(h),
    );
    return hasRequired && !hasExcluded;
  });
}

/**
 * Multi-file picker for sources whose export is split across several CSVs
 * (e.g. IMDb's ratings + watchlist). A single "choose files" input accepts
 * any number of files at once; each is auto-classified from its header row
 * and rendered as a row with a correctable type selector, since detection
 * can't always tell two similarly-shaped exports apart with certainty.
 */
export default function PreImportFiles({ sourceConfig, isLoading, onStart }) {
  const [entries, setEntries] = useState([]); // [{ id, file, type }]
  const { fileTypes, accept, buttonText, submitText } = sourceConfig.upload;

  const handleFilesChosen = async (event) => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = ""; // allow re-choosing the same file after removing it
    if (chosen.length === 0) return;

    const detected = await Promise.all(
      chosen.map(async (file) => {
        if (!file.name.toLowerCase().endsWith(accept.replace(".", ""))) {
          return { file, type: null };
        }
        const match = await detectFileType(file, fileTypes);
        return { file, type: match?.key ?? null };
      }),
    );

    const unrecognised = detected.filter((d) => !d.type);
    if (unrecognised.length > 0) {
      toast.error(
        `Couldn't recognise ${unrecognised.map((d) => d.file.name).join(", ")} — please check you selected the right file`,
        { autoClose: false },
      );
    }

    setEntries((prev) => {
      // A newly-chosen file replaces any existing entry of the same type,
      // rather than stacking up duplicates of the same slot.
      const byType = Object.fromEntries(prev.map((e) => [e.type, e]));
      detected
        .filter((d) => d.type)
        .forEach((d) => {
          byType[d.type] = {
            id: `${d.type}-${Date.now()}-${Math.random()}`,
            file: d.file,
            type: d.type,
          };
        });
      return Object.values(byType);
    });
  };

  const handleTypeChange = (id, newType) => {
    setEntries((prev) => {
      const withoutClash = prev.filter((e) => e.type !== newType || e.id === id);
      return withoutClash.map((e) =>
        e.id === id ? { ...e, type: newType } : e,
      );
    });
  };

  const handleRemove = (id) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleStart = () => {
    onStart(entries.map(({ file, type }) => ({ file, type })));
  };

  return (
    <Box sx={{ mt: 2 }}>
      {entries.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {entries.map((entry) => (
            <Stack
              key={entry.id}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={{ xs: 0.5, sm: 1 }}
              sx={{
                p: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ flexGrow: 1, minWidth: 0 }}
              >
                <InsertDriveFileOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", flexShrink: 0, mt: "2px" }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {entry.file.name}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                justifyContent={{ xs: "flex-end", sm: "flex-start" }}
              >
                <Select
                  size="small"
                  value={entry.type}
                  onChange={(e) => handleTypeChange(entry.id, e.target.value)}
                  disabled={isLoading}
                  sx={{
                    minWidth: { xs: 96, sm: 120 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  }}
                >
                  {fileTypes.map((type) => (
                    <MenuItem
                      key={type.key}
                      value={type.key}
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                    >
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                <IconButton
                  size="small"
                  onClick={() => handleRemove(entry.id)}
                  disabled={isLoading}
                  aria-label={`Remove ${entry.file.name}`}
                  sx={{ p: { xs: 0.5, sm: 1 } }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <input
          accept={accept}
          id={`${sourceConfig.key}-multi-upload`}
          aria-label={`Upload your ${sourceConfig.label} export files`}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFilesChosen}
        />
        <label htmlFor={`${sourceConfig.key}-multi-upload`}>
          <Button variant="outlined" component="span" disabled={isLoading}>
            {buttonText}
          </Button>
        </label>
        <Button
          variant="contained"
          disabled={isLoading || entries.length === 0}
          onClick={handleStart}
        >
          {isLoading ? "Uploading…" : submitText}
        </Button>
      </Box>
    </Box>
  );
}
